import store from "/@/store";
import { UserCredentials } from "../types/UserCredentials.type";
import { useToast } from "vue-toastification";
import { useRoute } from "vue-router";
import router from "/@/router";
import useAuthToken from "./useAuthToken";
import { StaticMessages } from "../enums/static-messages.enum";
import useUserData from "./useUserData";
import { setSentryUser, clearSentryUser } from "/@/plugins/sentry";
import { redirectOrHome } from "/@/router/redirect-target";

export default function useAuth() {
  const toast = useToast();
  const route = useRoute();
  const { storeToken, removeToken } = useAuthToken();
  const { getAuthUser } = useUserData();

  const authenticateUser = async (credentials: UserCredentials) => {
    try {
      const res = await store.dispatch("auth/login", credentials);

      storeToken(res.data.token);

      await getAuthUser();

      // Set Sentry user context (anonymized)
      const user = (store.state as any).auth.user;
      if (user?.id) {
        setSentryUser(user.id);
      }

      // Only follow internal app paths; anything else (absolute URLs,
      // protocol-relative //host) is an open-redirect phishing vector.
      // Pushed as a STRING on purpose: Vue Router keeps only the path when it
      // is given an object whose `path` carries a query, so an email deep link
      // to /claims/9?type=Funeral would arrive without the type it needs.
      router.push(redirectOrHome(route.query.redirect));
      toast.success(StaticMessages.LOGIN_SUCCESS);

      return true;
    } catch (err) {
      toast.error(err.response.data.message);
    }
  };

  const logoutUser = () => {
    // Clear Sentry user context
    clearSentryUser();

    removeToken();
    router.push({ name: "login" });
  };

  return {
    authenticateUser,
    logoutUser,
  };
}
